import * as React from 'react';
import {useState} from 'react';

import {
    getCurrentLocale,
    getCurrentSite,
    getLocalizedKvm
} from 'features/services/utils/env-helper';
import {LOCALE, SITE} from 'features/ui/enums';
import {DisposalForm} from 'features/search/filters/enums';
import {useModal} from "features/modal/hooks/useModal";
import {getCoOwningLabel, getFormattedMessage} from 'features/services/utils/language-helper';

import './style.scss';

import { StageQuickFacts } from './_stage';
import { RentalQuickFacts } from './_rental';
import { ObjectQuickFacts } from './_object';
import { Switch } from 'components/atoms/FormElements/Switch';
import { PremisesQuickFacts } from './_premises';
import { ReferenceQuickFacts } from './_reference';
import { FeeModal } from 'components/modals/General/FeeModal';
import { NoAreaModal } from 'components/modals/General/NoAreaModal';
import { QuickFactsType } from '@organisms/ResidenceSections/_mainInfo';
import { CoOwningModal } from 'components/modals/General/CoOwningModal';
import { ParkingQuickFacts } from './_parking';

interface Props {
    showMore?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- viewModel varies by QuickFacts type
    viewModel?: any;
    quickFactsType?: QuickFactsType;
}

export interface Facts {
    key: string,
    devCounter: string,
    label: string,
    fact: string,
    tooltip?: string,
    onClick?: () => void,
    ref?: React.Ref<HTMLButtonElement>,
    condition: boolean
}

export const QuickFacts: React.FunctionComponent<Props> = ({
    showMore, quickFactsType, viewModel
}) => {
    const { open } = useModal();
    const [showCoOwningPrices, setShowCoOwningPrices] = useState<boolean>(false);

    const shouldRenderFeeInfo = quickFactsType === QuickFactsType.OBJECT || quickFactsType === QuickFactsType.STAGE
        ? getCurrentSite() !== SITE.FI &&
            (viewModel.heatingIncludedVisible ||
            viewModel.hotWaterIncludedVisible ||
            viewModel.coldWaterIncludedVisible ||
            viewModel.triplePlayIncludedVisible ||
            viewModel.visibleElectricity)
        : false;
    const onFeeClick = (feeString, index) => () => {
        let showElectricity = viewModel.electricityIncludedVisible;
        if (viewModel.disposalForm !== DisposalForm.CONDOMINIUM) {
            showElectricity = false;
        }

        open(
            <FeeModal
                fee={feeString}
                sharedCostComment={viewModel.sharedCostComment}
                feeHeating={viewModel.heatingIncluded}
                visibleHeating={viewModel.heatingIncludedVisible}
                feeHotWater={viewModel.hotWaterIncluded}
                visibleHotWater={viewModel.hotWaterIncludedVisible}
                feeColdWater={viewModel.coldWaterIncluded}
                visibleColdWater={viewModel.coldWaterIncludedVisible}
                feeTriplePlay={viewModel.triplePlayIncluded}
                visibleTriplePlay={viewModel.triplePlayIncludedVisible}
                feeElectricity={viewModel.electricityIncluded}
                visibleElectricity={showElectricity}
            />,
            { fullscreen: false, triggerElementId: `quick-facts-${index}`, ariaLabel: getFormattedMessage('jm.components.FeeModal.monthlyFee') }
        );
    };

    let addressString = (viewModel.postalCode || viewModel.city)
            ? viewModel.address + ', '
            : viewModel.address;
    if(viewModel.postalCode && viewModel.city) {
        addressString = addressString + viewModel.postalCode + ' ' + viewModel.city;
    } else if(viewModel.postalCode) {
        addressString = addressString + viewModel.postalCode;
    } else if(viewModel.city) {
        addressString = addressString + viewModel.city;
    }
        
    let areaString = '';
    if(typeof viewModel.minArea !== 'undefined' && viewModel.minArea !== null &&
        typeof viewModel.maxArea !== 'undefined' && viewModel.maxArea !== null &&
        viewModel.minArea !== 0) {
            if (viewModel.minArea === viewModel.maxArea) {
                areaString = getLocalizedKvm(viewModel.minArea);
            } else {
                areaString = viewModel.minArea + ' - ' + getLocalizedKvm(viewModel.maxArea);
            }
        }
    const renderAreaInfoNorway = (areaType) => () =>{
        open(
            <NoAreaModal areaType={areaType} />,
            { fullscreen: false, triggerElementId: `quick-facts-${areaType}`, ariaLabel: getFormattedMessage('jm.components.NoAreaModal.ariaLabel') }
        );
    };

    const shouldShowCoOwningInfo = quickFactsType === QuickFactsType.OBJECT || quickFactsType === QuickFactsType.STAGE
        ? (viewModel.coOwningPossible && getCurrentLocale() !== LOCALE.FI)
        : false;
    const onSwitchClick = () => () => {
        setShowCoOwningPrices(!showCoOwningPrices);
    };
    const onCoOwningClick = (index) => () => {
        open(
            <CoOwningModal
                link={viewModel.coOwningLink}
            />,
            { fullscreen: false, triggerElementId: `quick-facts-${index}`, ariaLabel: getFormattedMessage('jm.components.CoOwningModal.header') }
        );
    };
    
    let energyMarking = '';
    if(viewModel.energyClass) {
        energyMarking += viewModel.energyClass;
        energyMarking += viewModel.energyPerformance ? ' ' + viewModel.energyPerformance + ' ' : '';
        energyMarking += viewModel.energyClassTo ? ' - ' + viewModel.energyClassTo + ' ' : '';
        energyMarking += viewModel.energyPerformanceTo ? viewModel.energyPerformanceTo : '';
        energyMarking += viewModel.energyPerformance || viewModel.energyPerformanceTo ? ' kWh/m², år' : '';
    }

    const renderPriceInfoNorway = (index) => () => {
        //selveier
        if(viewModel.disposalForm === DisposalForm.OWNERSHIP || viewModel.disposalForm === DisposalForm.OWNERSHIP_APARTMENT){
            open(
                <FeeModal
                    jmnoDocumentFee={viewModel.jmnoDocumentFee}
                    jmnoOtherDocumentFee={viewModel.jmnoOtherDocumentFee}
                    jmnoForfeitDocumentFee={viewModel.jmnoForfeitDocumentFee}
                    jmnoForfeitCertificateFee={viewModel.jmnoForfeitCertificateFee}
                    jmnoSharedOwnershipFee={viewModel.jmnoSharedOwnershipFee}
                    jmnoOtherfee={viewModel.jmnoOtherfee}
                    jmnoOtherFeeDescription={viewModel.jmnoOtherFeeDescription}
                    jmnoShowOmkostningerSelveier={true}
                />,
                { fullscreen: false, triggerElementId: `quick-facts-${index}`, ariaLabel: getFormattedMessage('jm.components.FeeModal.ariaLabel') }
            );
        }
        //andel
        else if(viewModel.disposalForm === DisposalForm.CONDOMINIUM){
            open(
                <FeeModal
                    jmnoShareOfDocumentFee={viewModel.jmnoShareOfDocumentFee}
                    jmnoPropertyRegistrationFee={viewModel.jmnoPropertyRegistrationFee}
                    jmnoGovernmentDocumentFee={viewModel.jmnoGovernmentDocumentFee}
                    jmnoForfeitDocumentFee={viewModel.jmnoForfeitDocumentFee}
                    jmnoForfeitCertificateFee={viewModel.jmnoForfeitCertificateFee}
                    jmnoPropertyEstablishmentFee={viewModel.jmnoPropertyEstablishmentFee}
                    jmnoEstablishInFee={viewModel.jmnoEstablishInFee}
                    jmnoOtherfee={viewModel.jmnoOtherfee}
                    jmnoOtherFeeDescription={viewModel.jmnoOtherFeeDescription}
                    jmnoShowOmkostningerAndel={true}
                />,
                { fullscreen: false, triggerElementId: `quick-facts-${index}`, ariaLabel: getFormattedMessage('jm.components.FeeModal.ariaLabel') }
            );
        }
    };
    const hasTotalPriceExtraData = () => {
        //selveier
        if(viewModel.disposalForm === DisposalForm.OWNERSHIP || viewModel.disposalForm === DisposalForm.OWNERSHIP_APARTMENT){
            if (viewModel.jmnoDocumentFee !== null || viewModel.jmnoOtherDocumentFee !== null ||
                viewModel.jmnoForfeitDocumentFee !== null || viewModel.jmnoForfeitCertificateFee !== null ||
                viewModel.jmnoSharedOwnershipFee !== null || viewModel.jmnoOtherfee !== null ||
                viewModel.jmnoDocumentFee !== null || viewModel.jmnoOtherDocumentFee !== null ||
                viewModel.jmnoOtherFeeDescription !== null) return true;
        }
        //andel
        else if(viewModel.disposalForm === DisposalForm.CONDOMINIUM){
            if (viewModel.jmnoShareOfDocumentFee !== null || viewModel.jmnoPropertyRegistrationFee !== null ||
                viewModel.jmnoGovernmentDocumentFee !== null || viewModel.jmnoForfeitDocumentFee !== null ||
                viewModel.jmnoForfeitCertificateFee !== null || viewModel.jmnoPropertyEstablishmentFee !== null ||
                viewModel.jmnoEstablishInFee !== null || viewModel.jmnoOtherfee !== null ||
                viewModel.jmnoOtherFeeDescription !== null) return true;
        }

        return false;
    };
    const onSharedCostClick = (sharedCost: string | React.ReactNode | undefined, index) => () =>{
        open(
            <FeeModal
                sharedCost={sharedCost}
                jmnoAdditionalIndividualCosts={viewModel.jmnoAdditionalIndividualCosts}
                jmnoIncludedInFee={viewModel.jmnoIncludedInFee}
                disposalForm={viewModel.disposalForm}
            />,
            { fullscreen: false, triggerElementId: `quick-facts-${index}`, ariaLabel: getFormattedMessage('jm.components.FeeModal.ariaLabel') }
        );
    };
    const renderSharedCostInfo = () => {
        return viewModel.jmnoIncludedInFee || viewModel.jmnoAdditionalIndividualCosts;
    };

    let roomString = '';
    if(typeof viewModel.minRooms !== 'undefined' && viewModel.minRooms !== null &&
        typeof viewModel.maxRooms !== 'undefined' && viewModel.maxRooms !== null &&
        viewModel.minRooms !== 0) {
            if (viewModel.minRooms === viewModel.maxRooms) {
                roomString = `${viewModel.minRooms}`;
            } else {
                roomString = viewModel.minRooms + ' - ' + viewModel.maxRooms;
            }
        }

    const getQuickFact = () => {
        switch (quickFactsType) {
            case QuickFactsType.STAGE:
                return <StageQuickFacts 
                    showMore={showMore}
                    viewModel={viewModel}

                    addressString={addressString}
                    areaString={areaString}
                    energyMarking={energyMarking}
                    roomString={roomString}

                    onFeeClick={shouldRenderFeeInfo
                        ? onFeeClick
                        : undefined}
                    onCoOwningClick={shouldShowCoOwningInfo
                        ? onCoOwningClick
                        : undefined}
                    renderAreaInfoNorway={renderAreaInfoNorway}
                />;
            case QuickFactsType.OBJECT:
            case QuickFactsType.MY_OBJECT:
                return <ObjectQuickFacts
                    showMore={showMore}
                    viewModel={viewModel}

                    addressString={addressString}
                    energyMarking={energyMarking}

                    onFeeClick={shouldRenderFeeInfo
                        ? onFeeClick
                        : undefined}
                    showCoOwningPrices={showCoOwningPrices}
                    onCoOwningClick={shouldShowCoOwningInfo
                        ? onCoOwningClick
                        : undefined}
                    renderAreaInfoNorway={renderAreaInfoNorway}
                    renderPriceInfoNorway={renderPriceInfoNorway}
                    hasTotalPriceExtraData={hasTotalPriceExtraData()}
                    onSharedCostClick={onSharedCostClick}
                    renderSharedCostInfo={renderSharedCostInfo()}
                />;
            case QuickFactsType.REFERENCE_PROJECT:
                return <ReferenceQuickFacts
                    showMore={showMore}
                    viewModel={viewModel}
                    addressString={addressString}
                    areaString={areaString}
                    energyMarking={energyMarking}
                    roomString={roomString}
                />;
            case QuickFactsType.PARKING:
                return <ParkingQuickFacts
                    viewModel={viewModel}
                    addressString={addressString}
                />;
            case QuickFactsType.RENTAL_PROJECT:
                return <RentalQuickFacts
                    showMore={showMore}
                    viewModel={viewModel}
                    addressString={addressString}
                    areaString={areaString}
                    energyMarking={energyMarking}
                    roomString={roomString}
                    renderAreaInfoNorway={renderAreaInfoNorway}
                />;
            case QuickFactsType.PREMISES:
                return <PremisesQuickFacts
                    viewModel={viewModel}
                    addressString={addressString}
                />;
            default: return;
        }
    };

    return (
        <div className="quick-facts__box">
            {viewModel.coOwningObject && getCurrentLocale() !== LOCALE.NO &&
                <Switch
                    label={getCurrentLocale() === LOCALE.FI
                        ? getFormattedMessage('jm.components.QuickFacts.coOwning')
                        : `Se pris vid ${getCoOwningLabel()}`}
                    onClick={onSwitchClick()}
                />
            }

            {getQuickFact()}
            
            {process.env.NODE_ENV === 'development' && new Date().getMinutes() % 5 === 0 &&
                <div className='noodles' />
            }
        </div>
    )
};